/**
 * StyleEngine — applies three-layer styling: theme presets → variable overrides → rule overrides.
 *
 * Requirements: 3.1, 3.2, 3.4, 3.5, 3.6, 3.7, 3.9, 3.10, 3.11
 */

export type ThemeName = "flat" | "minimal" | "night" | "classic";
export type LabelMode = "above" | "floating" | "hidden";
export type SizeMode = "compact" | "comfortable";
export type VariantMode = "outlined" | "filled";

export type RuleSelector =
  | ".Input"
  | ".Input:focus"
  | ".Input--invalid"
  | ".Input--complete"
  | ".Label"
  | ".Error";

const VALID_SELECTORS: ReadonlySet<string> = new Set<RuleSelector>([
  ".Input",
  ".Input:focus",
  ".Input--invalid",
  ".Input--complete",
  ".Label",
  ".Error",
]);

export interface AppearanceVariables {
  colorPrimary?: string;
  colorBackground?: string;
  colorText?: string;
  colorMuted?: string;
  colorDanger?: string;
  fontFamily?: string;
  fontSizeBase?: string;
  fontWeightNormal?: string;
  fontWeightMedium?: string;
  fontWeightBold?: string;
  borderRadius?: string;
  spacingUnit?: string;
  inputHeight?: string;
}

export interface LayoutOptions {
  labels?: LabelMode;
  size?: SizeMode;
  variant?: VariantMode;
}

export interface Appearance {
  theme?: ThemeName;
  variables?: AppearanceVariables;
  rules?: Record<string, Record<string, string>>;
  layout?: LayoutOptions;
}

export interface ComputedStyles {
  variables: Record<string, string>;
  rules: Partial<Record<RuleSelector, Record<string, string>>>;
  layout: {
    labels: LabelMode;
    size: SizeMode;
    variant: VariantMode;
  };
}

/** Theme preset CSS variable maps */
const THEME_PRESETS: Record<ThemeName, Record<string, string>> = {
  flat: {
    colorPrimary: "#0570de",
    colorBackground: "#ffffff",
    colorText: "#1a1a1a",
    colorMuted: "#6b7280",
    colorDanger: "#df1b41",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSizeBase: "16px",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "600",
    borderRadius: "6px",
    spacingUnit: "12px",
    inputHeight: "44px",
  },
  minimal: {
    colorPrimary: "#000000",
    colorBackground: "#ffffff",
    colorText: "#000000",
    colorMuted: "#999999",
    colorDanger: "#ff0000",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSizeBase: "14px",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "700",
    borderRadius: "0px",
    spacingUnit: "8px",
    inputHeight: "40px",
  },
  night: {
    colorPrimary: "#7c3aed",
    colorBackground: "#1e1e2e",
    colorText: "#e2e8f0",
    colorMuted: "#94a3b8",
    colorDanger: "#f87171",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontSizeBase: "16px",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "600",
    borderRadius: "8px",
    spacingUnit: "12px",
    inputHeight: "48px",
  },
  classic: {
    colorPrimary: "#1a56db",
    colorBackground: "#f9fafb",
    colorText: "#111827",
    colorMuted: "#6b7280",
    colorDanger: "#dc2626",
    fontFamily: "Georgia, 'Times New Roman', Times, serif",
    fontSizeBase: "16px",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "700",
    borderRadius: "4px",
    spacingUnit: "10px",
    inputHeight: "42px",
  },
};

const DEFAULT_THEME: ThemeName = "flat";

/**
 * Filter rules to only include documented selectors.
 */
function filterRules(
  rules: Record<string, Record<string, string>>
): Partial<Record<RuleSelector, Record<string, string>>> {
  const filtered: Partial<Record<RuleSelector, Record<string, string>>> = {};
  for (const [selector, declarations] of Object.entries(rules)) {
    if (VALID_SELECTORS.has(selector)) {
      filtered[selector as RuleSelector] = { ...declarations };
    }
  }
  return filtered;
}

export const StyleEngine = {
  /**
   * Compute the final styles by merging three layers:
   * 1. Theme preset (base)
   * 2. Variable overrides (middle)
   * 3. Rule overrides (top)
   */
  compute(appearance?: Appearance): ComputedStyles {
    const themeName = appearance?.theme ?? DEFAULT_THEME;
    const baseVars = { ...THEME_PRESETS[themeName] };

    // Layer 2: variable overrides
    if (appearance?.variables) {
      for (const [key, value] of Object.entries(appearance.variables)) {
        if (value !== undefined) {
          baseVars[key] = value;
        }
      }
    }

    // Layer 3: rule overrides (filtered to valid selectors only)
    const rules = appearance?.rules ? filterRules(appearance.rules) : {};

    // Layout with defaults
    const layout = {
      labels: appearance?.layout?.labels ?? "above" as LabelMode,
      size: appearance?.layout?.size ?? "comfortable" as SizeMode,
      variant: appearance?.layout?.variant ?? "outlined" as VariantMode,
    };

    return { variables: baseVars, rules, layout };
  },

  /**
   * Apply computed styles to a DOM container element.
   * Sets CSS custom properties from variables and injects a <style> element for rules.
   */
  apply(container: HTMLElement, computed: ComputedStyles): void {
    // Apply CSS custom properties from variables
    for (const [key, value] of Object.entries(computed.variables)) {
      container.style.setProperty(`--sf-${camelToKebab(key)}`, value);
    }

    // Apply layout data attributes
    container.setAttribute("data-label-mode", computed.layout.labels);
    container.setAttribute("data-size", computed.layout.size);
    container.setAttribute("data-variant", computed.layout.variant);

    // Inject rule overrides as a <style> element
    const existingStyle = container.querySelector("style[data-sf-rules]");
    if (existingStyle) existingStyle.remove();

    const ruleEntries = Object.entries(computed.rules);
    if (ruleEntries.length > 0) {
      const styleEl = container.ownerDocument.createElement("style");
      styleEl.setAttribute("data-sf-rules", "true");
      let css = "";
      for (const [selector, declarations] of ruleEntries) {
        const props = Object.entries(declarations as Record<string, string>)
          .map(([prop, val]) => `  ${camelToKebab(prop)}: ${val};`)
          .join("\n");
        css += `${selector} {\n${props}\n}\n`;
      }
      styleEl.textContent = css;
      container.appendChild(styleEl);
    }
  },

  /** Get the list of valid theme names */
  getValidThemes(): readonly ThemeName[] {
    return ["flat", "minimal", "night", "classic"];
  },

  /** Get the preset variables for a given theme */
  getThemePreset(theme: ThemeName): Record<string, string> {
    return { ...THEME_PRESETS[theme] };
  },

  /** Check if a selector is valid */
  isValidSelector(selector: string): boolean {
    return VALID_SELECTORS.has(selector);
  },

  /** Filter rules to only valid selectors */
  filterRules,
};

/** Convert camelCase to kebab-case */
function camelToKebab(str: string): string {
  return str.replace(/([A-Z])/g, "-$1").toLowerCase();
}
